// src/components/PeopleAnalytics.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebaseConfig';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs, doc, getDoc, orderBy, limit } from 'firebase/firestore';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, LineChart, Line,
    CartesianGrid, PieChart, Pie, Cell, ScatterChart, Scatter, ZAxis, AreaChart, Area
} from 'recharts';
import {
    FaUsers, FaCakeCandles, FaBuilding, FaFaceSmile, FaThumbsUp,
    FaFilter, FaArrowTrendDown, FaRocket, FaWandMagicSparkles, FaTrash,
    FaDoorOpen, FaBriefcase, FaCalendarMinus, FaFileExcel,
    FaArrowUp, FaArrowRight, FaArrowsUpDown, FaArrowsLeftRight,
    FaCalendarDays, FaFloppyDisk, FaGear, FaEye, FaEyeSlash,
    FaXmark, FaGripVertical, FaRotateLeft, FaCheck, FaPencil,
    FaPlus, FaCircleInfo, FaScrewdriverWrench, FaMagnifyingGlass,
    FaChartLine, FaChartBar, FaTableCells
} from 'react-icons/fa6';
import { IoTimerOutline } from "react-icons/io5";
import '../styles/PeopleAnalytics.css';

// =============================================================================
// === CONSTANTS ===============================================================
// =============================================================================

const SECTION_META = {
    kpiNumbers:     { label: 'KPIs Principais',          icon: '📊' },
    highlights:     { label: 'Destaques do Período',     icon: '⭐' },
    nineBox:        { label: 'Matriz 9-Box Dinâmica',    icon: '🔲' },
    standardCharts: { label: 'Gráficos de Equipe',       icon: '📈' },
    absenteeism:    { label: 'Absenteísmo',              icon: '📅' },
    exits:          { label: 'Análise de Desligamentos', icon: '🚪' },
};

const PERIOD_PRESETS = [
    { key: '1m',     label: '1M' },
    { key: '3m',     label: '3M' },
    { key: '6m',     label: '6M' },
    { key: '12m',    label: '12M' },
    { key: 'ytd',    label: 'Ano' },
    { key: 'custom', label: 'Custom' },
];

const PIE_COLORS = ['var(--primary-color, #002454)', 'var(--secondary-color, #8dbefd)', '#ff6361', '#ffa600', '#00b894', '#6c5ce7', '#e17055', '#fd79a8'];

// 9-Box: [yTier=0 Low → yTier=2 High][xTier=0 Low → xTier=2 High]
const NINE_BOX_QUADRANTS = [
    [
        { name: 'Baixo Impacto',      desc: 'Baixo em ambas dimensões. Requer ação imediata.',           color: '#fee2e2' },
        { name: 'Em Desenvolvimento', desc: 'Desempenho médio, potencial a ser construído.',             color: '#fef3c7' },
        { name: 'Especialista',       desc: 'Alta performance, foco especializado. Valorizar.',          color: '#fef9c3' },
    ],
    [
        { name: 'Em Risco',           desc: 'Alto potencial, desempenho abaixo. Investigar.',            color: '#ffedd5' },
        { name: 'Núcleo Sólido',      desc: 'Equilibrado e confiável. Pilar da operação.',               color: '#f5f5f5' },
        { name: 'Destaque',           desc: 'Alta performance. Candidato a liderança.',                  color: '#dcfce7' },
    ],
    [
        { name: 'Diamante Bruto',     desc: 'Enorme potencial, desempenho baixo. Desenvolver urgente.',  color: '#fce7f3' },
        { name: 'Alto Potencial',     desc: 'Grande potencial e bom desempenho. Planejar crescimento.',  color: '#dbeafe' },
        { name: 'Estrela',            desc: 'Top performer. Reter, reconhecer e expor a desafios.',      color: '#bbf7d0' },
    ],
];

const KPI_CATALOG = {
    headcount:         { defaultTitle: 'Headcount Ativo',      icon: 'users',      getValue: d => d?.kpis?.headcount ?? 0,        getStatus: () => 'neutral' },
    turnover:          { defaultTitle: 'Turnover do Período',   icon: 'trend-down', getValue: d => `${d?.kpis?.turnoverRate ?? 0}%`, getStatus: d => { const v = parseFloat(d?.kpis?.turnoverRate); return v > 20 ? 'alert' : v > 10 ? 'warning' : 'neutral'; } },
    avgSalary:         { defaultTitle: 'Salário Médio',         icon: 'salary',     getValue: d => (d?.kpis?.avgSalary ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), getStatus: () => 'neutral' },
    avgMood:           { defaultTitle: 'Humor Médio',           icon: 'mood',       getValue: d => d?.kpis?.avgMood ?? 'N/A',      getStatus: d => { const v = parseFloat(d?.kpis?.avgMood); return v > 4 ? 'good' : v < 3 ? 'alert' : 'warning'; } },
    absenteeismRate:   { defaultTitle: 'Taxa de Absenteísmo',   icon: 'calendar',   getValue: d => `${d?.kpis?.absenteeismRate ?? 0}%`, getStatus: d => { const v = parseFloat(d?.kpis?.absenteeismRate); return v > 5 ? 'alert' : v > 2 ? 'warning' : 'good'; } },
    lostHours:         { defaultTitle: 'Horas Perdidas',        icon: 'timer',      getValue: d => `${d?.kpis?.lostHours ?? 0}h`, getStatus: () => 'neutral' },
    absencesCount:     { defaultTitle: 'Ocorrências de Falta',  icon: 'file',       getValue: d => d?.kpis?.absencesCount ?? 0,   getStatus: () => 'neutral' },
    rampUpCount:       { defaultTitle: 'Novas Entradas',        icon: 'rocket',     getValue: d => d?.kpis?.rampUpCount ?? 0,     getStatus: () => 'good' },
    feedbacksTotal:    { defaultTitle: 'Total de Feedbacks',    icon: 'thumbs',     getValue: d => d?.kpis?.feedbacksTotal ?? 0,  getStatus: () => 'neutral' },
    avgTenure:         { defaultTitle: 'Tempo Médio de Casa',   icon: 'building',   getValue: d => d?.kpis?.avgTenureMonths ? `${d.kpis.avgTenureMonths}m` : 'N/A', getStatus: () => 'neutral' },
    exitCount:         { defaultTitle: 'Desligamentos',         icon: 'door',       getValue: d => d?.kpis?.exitCount ?? 0,       getStatus: d => { const v = d?.kpis?.exitCount ?? 0; return v > 5 ? 'alert' : v > 2 ? 'warning' : 'neutral'; } },
    birthdayCount:     { defaultTitle: 'Aniversariantes (Mês)', icon: 'cake',       getValue: d => d?.kpis?.monthlyBirthdays?.length ?? 0, getStatus: () => 'neutral' },
    totalSalaryMass:   { defaultTitle: 'Massa Salarial Total',  icon: 'salary',     getValue: d => (d?.kpis?.totalSalaryMass ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), getStatus: () => 'neutral' },
    voluntaryTurnover: { defaultTitle: 'Turnover Voluntário',   icon: 'door',       getValue: d => d?.insights?.voluntaryTurnover ?? '0%', getStatus: () => 'warning' },
    avgEvalScore:      { defaultTitle: 'Nota Média Avaliação',  icon: 'chart-line', getValue: d => d?.kpis?.avgEvalScore ? d.kpis.avgEvalScore.toFixed(1) : 'N/A', getStatus: d => { const v = d?.kpis?.avgEvalScore; return !v ? 'neutral' : v >= 4 ? 'good' : v >= 2.5 ? 'warning' : 'alert'; } },
};

const DEFAULT_KPI_CARDS = [
    { id: 'kpi-1', metricKey: 'headcount' },
    { id: 'kpi-2', metricKey: 'turnover' },
    { id: 'kpi-3', metricKey: 'avgSalary' },
    { id: 'kpi-4', metricKey: 'avgMood' },
];

const DEFAULT_DASHBOARD_CONFIG = {
    sections: {
        kpiNumbers:     { visible: true,  order: 0 },
        highlights:     { visible: true,  order: 1 },
        nineBox:        { visible: true,  order: 2 },
        standardCharts: { visible: true,  order: 3 },
        absenteeism:    { visible: true,  order: 4 },
        exits:          { visible: true,  order: 5 },
    },
    kpiCards: DEFAULT_KPI_CARDS,
    sectionTitles: {},
    annotations: {},
};

// =============================================================================
// === HELPERS =================================================================
// =============================================================================

const getDateObject = (d) => {
    if (!d) return null;
    if (d.toDate) return d.toDate();
    if (d instanceof Date) return d;
    const dt = new Date(d); return isNaN(dt.getTime()) ? null : dt;
};

const formatDateVisual = (d, opts = {}) => {
    const dt = getDateObject(d);
    if (!dt) return 'N/A';
    return dt.toLocaleDateString('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric', ...opts });
};

const formatMetricValue = (key, val) => {
    if (val == null) return 'N/A';
    const v = Number(val);
    if (key === 'salario') return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
    if (key === 'tempoCasaMeses') return `${Math.round(v)}m`;
    if (key === 'idade') return `${Math.round(v)} anos`;
    if (key === 'horasPerdidas') return `${v.toFixed(1)}h`;
    if (key === 'humorMedio' || key === 'notaAvaliacao') return v.toFixed(1);
    return isNaN(v) ? String(val) : String(Math.round(v * 10) / 10);
};

const calcTurnover = (users, s, e) => {
    if (!users?.length) return 0;
    const t = users.filter(u => { const d = getDateObject(u.dataInativacao); return d && d >= s && d <= e; }).length;
    const sh = users.filter(u => { const en = getDateObject(u.dataEntrada), ex = getDateObject(u.dataInativacao); return en && en < s && (!ex || ex > s); }).length;
    const eh = users.filter(u => { const en = getDateObject(u.dataEntrada), ex = getDateObject(u.dataInativacao); return en && en <= e && (!ex || ex > e); }).length;
    const avg = (sh + eh) / 2;
    return avg === 0 ? 0 : ((t / avg) * 100).toFixed(1);
};

const calcAbsent = (list, count, s, e) => {
    if (!count) return { rate: 0, lostHours: 0, count: 0, data: [] };
    const data = list.filter(a => { const d = getDateObject(a.date); return d && d >= s && d <= e; });
    const hrs = data.reduce((acc, a) => acc + (parseFloat(a.hours) || 0), 0);
    return { rate: ((hrs / (count * 176)) * 100).toFixed(2), lostHours: hrs.toFixed(1), count: data.length, data };
};

const getHCOverTime = (users, s, e) => {
    const months = Math.min(Math.max(1, Math.round((e - s) / (1000 * 60 * 60 * 24 * 30))), 24);
    const data = [];
    for (let i = months - 1; i >= 0; i--) {
        const d = new Date(e.getFullYear(), e.getMonth() - i + 1, 0);
        if (d < s) continue;
        data.push({
            name: d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
            headcount: users.filter(u => { const en = getDateObject(u.dataEntrada), ex = getDateObject(u.dataInativacao); return en && en <= d && (!ex || ex > d); }).length
        });
    }
    return data;
};

const getPeriod = (f) => {
    const e = new Date(); e.setHours(23, 59, 59, 999);
    if (f.preset === 'custom') {
        const s = f.customStart ? new Date(f.customStart + 'T00:00:00') : new Date(e.getFullYear() - 1, e.getMonth(), e.getDate());
        const ce = f.customEnd ? new Date(f.customEnd + 'T23:59:59') : e;
        return { start: s, end: ce, label: 'Período Personalizado' };
    }
    if (f.preset === 'ytd') return { start: new Date(e.getFullYear(), 0, 1), end: e, label: 'Ano Atual' };
    const m = { '1m': [1, 'Último Mês'], '3m': [3, 'Últimos 3 Meses'], '6m': [6, 'Últimos 6 Meses'], '12m': [12, 'Últimos 12 Meses'] };
    const [mo, lb] = m[f.preset] || m['12m'];
    return { start: new Date(e.getFullYear(), e.getMonth() - mo, e.getDate()), end: e, label: lb };
};

const loadCfg = () => {
    try {
        const s = localStorage.getItem('pa_cfg_v3');
        if (!s) return DEFAULT_DASHBOARD_CONFIG;
        const p = JSON.parse(s);
        return { ...DEFAULT_DASHBOARD_CONFIG, ...p, sections: { ...DEFAULT_DASHBOARD_CONFIG.sections, ...(p.sections || {}) }, kpiCards: p.kpiCards || DEFAULT_KPI_CARDS };
    } catch { return DEFAULT_DASHBOARD_CONFIG; }
};

const genId = () => Math.random().toString(36).slice(2, 9);

// =============================================================================
// === ICON RENDERER ===========================================================
// =============================================================================

const KpiIcon = ({ type }) => ({ users: <FaUsers />, 'trend-down': <FaArrowTrendDown />, salary: <IoTimerOutline />, mood: <FaFaceSmile />, calendar: <FaCalendarMinus />, timer: <IoTimerOutline />, file: <FaFileExcel />, rocket: <FaRocket />, thumbs: <FaThumbsUp />, building: <FaBuilding />, door: <FaDoorOpen />, cake: <FaCakeCandles />, briefcase: <FaBriefcase />, 'chart-line': <FaChartLine /> }[type] || <FaUsers />);

// =============================================================================
// === COMPONENTS ==============================================================
// =============================================================================

const KpiCard = ({ title, value, icon, status = 'neutral', isEditMode, onDelete, onEdit }) => (
    <div className={`kpi-card status-${status} ${isEditMode ? 'kpi-editable' : ''}`}>
        {isEditMode && <div className="kpi-em-grip"><FaGripVertical /></div>}
        <div className="kpi-icon-wrapper">{icon}</div>
        <div className="kpi-content">
            <span className="kpi-title">{title}</span>
            <div className="kpi-value">{value}</div>
        </div>
        {isEditMode && (
            <div className="kpi-em-actions">
                <button className="kpi-em-btn edit" onClick={e => { e.stopPropagation(); onEdit?.(); }} title="Editar"><FaPencil /></button>
                <button className="kpi-em-btn del" onClick={e => { e.stopPropagation(); onDelete?.(); }} title="Remover"><FaTrash /></button>
            </div>
        )}
    </div>
);

const ChartCard = ({ title, children, description, onDelete, icon }) => (
    <div className="chart-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    {icon && <span style={{ fontSize: '1.1rem' }}>{icon}</span>}
                    <h3 className="chart-title" style={{ margin: 0 }}>{title}</h3>
                </div>
                {description && <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem', lineHeight: 1.4 }}>{description}</p>}
            </div>
            {onDelete && <button onClick={onDelete} className="icon-btn-delete"><FaTrash /></button>}
        </div>
        <div className="chart-content">{children}</div>
    </div>
);

const DetailedListCard = ({ title, icon, items, detailKey = 'detail' }) => {
    const [page, setPage] = useState(1);
    const pp = 4;
    useEffect(() => setPage(1), [items]);
    const total = Math.ceil(items.length / pp);
    const paged = items.slice((page - 1) * pp, page * pp);
    return (
        <div className="detailed-list-card">
            <div className="kpi-card-header">
                <div className="kpi-icon-wrapper">{icon}</div>
                <span className="kpi-title">{title}</span>
            </div>
            {items.length > 0 ? (<>
                <ul className="detailed-list">
                    {paged.map((item, i) => (
                        <li key={i} className="detailed-list-item">
                            {item.photoURL ? <img src={item.photoURL} alt={item.name} className="user-photo" /> : <div className="user-photo-placeholder">{item.name?.charAt(0) || '?'}</div>}
                            <div className="user-info">
                                <span className="user-name">{item.name}</span>
                                {item[detailKey] && <span className="user-detail">{item[detailKey]}</span>}
                            </div>
                        </li>
                    ))}
                </ul>
                {total > 1 && (
                    <div className="pagination-controls">
                        <button className="pagination-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>&lt;</button>
                        <span>{page} / {total}</span>
                        <button className="pagination-btn" onClick={() => setPage(p => Math.min(total, p + 1))} disabled={page === total}>&gt;</button>
                    </div>
                )}
            </>) : <p className="no-data-text">Nenhum registro encontrado.</p>}
        </div>
    );
};

const AnnotationBlock = ({ text, isEditMode, onEdit }) => (
    <div className={`annotation-block ${isEditMode ? 'annotation-editable' : ''}`}>
        <FaCircleInfo className="annotation-icon" />
        <span className="annotation-text">{text}</span>
        {isEditMode && <button className="annotation-edit-btn" onClick={onEdit}><FaPencil /></button>}
    </div>
);

// =============================================================================
// === ENHANCED 9-BOX ==========================================================
// =============================================================================

const DynamicNineBox = ({ users, config, setConfig }) => {
    const [tooltip, setTooltip] = useState(null);

    const METRIC_LABELS = {
        salario: 'Salário (R$)', tempoCasaMeses: 'Tempo de Casa (m)', idade: 'Idade (anos)',
        feedbacksRecebidos: 'Feedbacks Recebidos', horasPerdidas: 'Absenteísmo (h)',
        humorMedio: 'Humor Médio (1-5)', notaAvaliacao: 'Nota de Avaliação',
    };

    if (!users?.length) return <p className="no-data-text" style={{ padding: '2rem', textAlign: 'center' }}>Sem dados suficientes.</p>;

    const valid = users.filter(u => u[config.x] != null && u[config.y] != null);
    if (!valid.length) return <div className="empty-state-box">Nenhum colaborador possui dados nos dois eixos selecionados.</div>;

    const xs = valid.map(u => u[config.x]), ys = valid.map(u => u[config.y]);
    const xMin = Math.min(...xs), xMax = Math.max(...xs);
    const yMin = Math.min(...ys), yMax = Math.max(...ys);
    const xStep = ((xMax - xMin) / 3) || 1;
    const yStep = ((yMax - yMin) / 3) || 1;

    const getTier = (val, min, step) => Math.min(2, Math.floor((val - min) / step));

    const grid = Array(3).fill(null).map(() => Array(3).fill(null).map(() => []));
    valid.forEach(u => { grid[getTier(u[config.y], yMin, yStep)][getTier(u[config.x], xMin, xStep)].push(u); });

    const getTierRange = (min, step, idx) => ({
        lo: min + idx * step,
        hi: idx === 2 ? min + 3 * step : min + (idx + 1) * step,
    });

    const totalUsers = valid.length;

    return (
        <div className="dynamic-nine-box-wrapper">
            {/* CONTROLS */}
            <div className="dynamic-nine-box-controls modern-controls">
                <div className="control-group">
                    <div className="control-label-wrapper"><FaArrowsUpDown className="control-icon" /><label>Eixo Y (Vertical)</label></div>
                    <select value={config.y} onChange={e => setConfig({ ...config, y: e.target.value })} className="modern-select">
                        {Object.entries(METRIC_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                </div>
                <div className="control-divider" />
                <div className="control-group">
                    <div className="control-label-wrapper"><FaArrowsLeftRight className="control-icon" /><label>Eixo X (Horizontal)</label></div>
                    <select value={config.x} onChange={e => setConfig({ ...config, x: e.target.value })} className="modern-select">
                        {Object.entries(METRIC_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                </div>
            </div>

            {/* MATRIX */}
            <div className="dynamic-nine-box-layout">
                <div className="y-axis-container">
                    <span className="axis-limit top">Alto</span>
                    <div className="nine-box-y-label"><FaArrowUp className="axis-arrow-y" /><span>{METRIC_LABELS[config.y]}</span></div>
                    <span className="axis-limit bottom">Baixo</span>
                </div>
                <div className="grid-and-x-axis">
                    <div className="dynamic-nine-box-grid">
                        {grid.slice().reverse().map((row, yRevIdx) => {
                            const yIdx = 2 - yRevIdx;
                            return row.map((cellUsers, xIdx) => {
                                const q = NINE_BOX_QUADRANTS[yIdx][xIdx];
                                const xR = getTierRange(xMin, xStep, xIdx);
                                const yR = getTierRange(yMin, yStep, yIdx);
                                const pct = totalUsers > 0 ? ((cellUsers.length / totalUsers) * 100).toFixed(0) : 0;
                                return (
                                    <div key={`${yRevIdx}-${xIdx}`} className="modern-cell nine-box-cell" style={{ backgroundColor: q.color }}>
                                        {/* Header: name + count */}
                                        <div className="cell-header-info">
                                            <span className="cell-quadrant-name">{q.name}</span>
                                            <span className="cell-count-badge" title={`${pct}% da equipe`}>{cellUsers.length}</span>
                                        </div>
                                        {/* Value ranges */}
                                        <div className="cell-ranges">
                                            <span className="cell-range-row">
                                                <span className="cell-range-axis-label">X</span>
                                                <span className="cell-range-vals">{formatMetricValue(config.x, xR.lo)} – {formatMetricValue(config.x, xR.hi)}</span>
                                            </span>
                                            <span className="cell-range-row">
                                                <span className="cell-range-axis-label">Y</span>
                                                <span className="cell-range-vals">{formatMetricValue(config.y, yR.lo)} – {formatMetricValue(config.y, yR.hi)}</span>
                                            </span>
                                        </div>
                                        {/* User capsules */}
                                        <div className="cell-users">
                                            {cellUsers.map(u => (
                                                <div key={u.id} className="user-capsule"
                                                    onMouseEnter={ev => setTooltip({ u, x: ev.clientX, y: ev.clientY })}
                                                    onMouseLeave={() => setTooltip(null)}>
                                                    {u.photoURL ? <img src={u.photoURL} alt={u.name} /> : <span>{u.name?.charAt(0)}</span>}
                                                </div>
                                            ))}
                                        </div>
                                        {/* Percentage footer */}
                                        {cellUsers.length > 0 && (
                                            <div className="cell-pct-bar">
                                                <div className="cell-pct-fill" style={{ width: `${pct}%` }} />
                                                <span className="cell-pct-label">{pct}%</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            });
                        })}
                    </div>
                    <div className="x-axis-container">
                        <span className="axis-limit left">Baixo</span>
                        <div className="nine-box-x-label"><span>{METRIC_LABELS[config.x]}</span><FaArrowRight className="axis-arrow-x" /></div>
                        <span className="axis-limit right">Alto</span>
                    </div>
                </div>
            </div>

            {/* FLOATING TOOLTIP */}
            {tooltip && (
                <div className="nine-box-tooltip" style={{ top: tooltip.y - 80, left: tooltip.x + 14 }}>
                    <strong>{tooltip.u.name}</strong>
                    <span>{METRIC_LABELS[config.x]}: <b>{formatMetricValue(config.x, tooltip.u[config.x])}</b></span>
                    <span>{METRIC_LABELS[config.y]}: <b>{formatMetricValue(config.y, tooltip.u[config.y])}</b></span>
                </div>
            )}

            {/* QUADRANT LEGEND */}
            <div className="nine-box-legend">
                <h4 className="nine-box-legend-title">Legenda dos Quadrantes</h4>
                <div className="nine-box-legend-grid">
                    {/* Top to bottom = High Y first */}
                    {[2, 1, 0].map(yIdx =>
                        [0, 1, 2].map(xIdx => {
                            const q = NINE_BOX_QUADRANTS[yIdx][xIdx];
                            const count = grid[yIdx][xIdx].length;
                            const pct = totalUsers > 0 ? ((count / totalUsers) * 100).toFixed(0) : 0;
                            return (
                                <div key={`${yIdx}-${xIdx}`} className="legend-cell" style={{ backgroundColor: q.color }}>
                                    <div className="legend-cell-head">
                                        <span className="legend-cell-name">{q.name}</span>
                                        <span className="legend-cell-count">{count} ({pct}%)</span>
                                    </div>
                                    <span className="legend-cell-desc">{q.desc}</span>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

// Dynamic AI Widget
const DynamicWidget = ({ config }) => {
    const { type, data, dataKey, xAxisKey, colors, columns, rows, kpiValue, kpiLabel, xAxisLabel, yAxisLabel } = config;
    const cc = colors || ['var(--primary-color,#002454)', 'var(--secondary-color,#8dbefd)', '#ff6361', '#ffa600', '#00b894'];
    if (type === 'list') return (<div className="dynamic-list-container"><table className="dynamic-table"><thead><tr>{columns.map((c, i) => <th key={i}>{c.label}</th>)}</tr></thead><tbody>{rows.map((r, i) => <tr key={i}>{columns.map((c, j) => <td key={j}>{r[c.key]}</td>)}</tr>)}</tbody></table></div>);
    if (type === 'kpi') return (<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}><span style={{ fontSize: '3rem', fontWeight: 800, color: cc[0] }}>{kpiValue}</span><span style={{ fontSize: '1rem', color: '#666', textTransform: 'uppercase' }}>{kpiLabel}</span></div>);
    if (type === 'bar') return (<ResponsiveContainer width="100%" height={300}><BarChart data={data}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey={xAxisKey || 'name'} tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} /><Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,.1)' }} /><Legend /><Bar dataKey={dataKey || 'value'} fill={cc[0]} radius={[4, 4, 0, 0]} name={config.description || 'Valor'} /></BarChart></ResponsiveContainer>);
    if (type === 'line' || type === 'area') { const CC = type === 'line' ? LineChart : AreaChart; const DC = type === 'line' ? Line : Area; return (<ResponsiveContainer width="100%" height={300}><CC data={data}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey={xAxisKey || 'name'} tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} /><Tooltip contentStyle={{ borderRadius: 8, border: 'none' }} /><Legend /><DC type="monotone" dataKey={dataKey || 'value'} stroke={cc[0]} fill={type === 'area' ? cc[1] : undefined} strokeWidth={3} /></CC></ResponsiveContainer>); }
    if (type === 'pie') return (<ResponsiveContainer width="100%" height={300}><PieChart><Pie data={data} cx="50%" cy="50%" innerRadius={60} outerRadius={80} dataKey={dataKey || 'value'} paddingAngle={5} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>{data.map((_, i) => <Cell key={i} fill={cc[i % cc.length]} />)}</Pie><Tooltip contentStyle={{ borderRadius: 8, border: 'none' }} /><Legend verticalAlign="bottom" height={36} /></PieChart></ResponsiveContainer>);
    return <p style={{ color: '#999', textAlign: 'center', marginTop: 40 }}>Visualização indisponível.</p>;
};

// =============================================================================
// === KPI EDIT MODAL ==========================================================
// =============================================================================

const KpiEditModal = ({ card, onSave, onClose }) => {
    const [metricKey, setMetricKey] = useState(card.metricKey);
    const [customTitle, setCustomTitle] = useState(card.customTitle || '');
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-card" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Editar KPI</h3>
                    <button className="modal-close-btn" onClick={onClose}><FaXmark /></button>
                </div>
                <div className="modal-body">
                    <div className="form-group">
                        <label>Métrica</label>
                        <select value={metricKey} onChange={e => setMetricKey(e.target.value)} className="modern-select" style={{ width: '100%' }}>
                            {Object.entries(KPI_CATALOG).map(([k, v]) => <option key={k} value={k}>{v.defaultTitle}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Título personalizado <span style={{ fontWeight: 400, color: '#999' }}>(opcional)</span></label>
                        <input type="text" className="form-input" placeholder={KPI_CATALOG[metricKey]?.defaultTitle} value={customTitle} onChange={e => setCustomTitle(e.target.value)} />
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose}>Cancelar</button>
                    <button className="btn-primary" onClick={() => onSave({ ...card, metricKey, customTitle: customTitle || undefined })}><FaCheck /> Aplicar</button>
                </div>
            </div>
        </div>
    );
};

// =============================================================================
// === CUSTOMIZE PANEL =========================================================
// =============================================================================

const CustomizePanel = ({ isOpen, onClose, dashboardConfig, onConfigChange, onSave, onReset }) => {
    const [tab, setTab] = useState('layout');
    const [search, setSearch] = useState('');
    const [secDrag, setSecDrag] = useState(null);
    const [secDragOver, setSecDragOver] = useState(null);
    const [kpiDrag, setKpiDrag] = useState(null);
    const [kpiDragOver, setKpiDragOver] = useState(null);

    const sorted = Object.entries(dashboardConfig.sections).sort((a, b) => a[1].order - b[1].order).map(([k]) => k);

    const secDragHandlers = (key) => ({
        draggable: true,
        onDragStart: (e) => { setSecDrag(key); e.dataTransfer.effectAllowed = 'move'; },
        onDragOver: (e) => { e.preventDefault(); if (key !== secDrag) setSecDragOver(key); },
        onDrop: (e) => {
            e.preventDefault();
            if (!secDrag || secDrag === key) return;
            const ns = { ...dashboardConfig.sections };
            const do_ = ns[secDrag].order, to = ns[key].order;
            Object.keys(ns).forEach(k => {
                const o = ns[k].order;
                if (do_ < to) { if (o > do_ && o <= to) ns[k] = { ...ns[k], order: o - 1 }; }
                else { if (o >= to && o < do_) ns[k] = { ...ns[k], order: o + 1 }; }
            });
            ns[secDrag] = { ...ns[secDrag], order: to };
            onConfigChange({ ...dashboardConfig, sections: ns });
            setSecDrag(null); setSecDragOver(null);
        },
        onDragEnd: () => { setSecDrag(null); setSecDragOver(null); }
    });

    const kpiDragHandlers = (id) => ({
        draggable: true,
        onDragStart: (e) => { setKpiDrag(id); e.dataTransfer.effectAllowed = 'move'; },
        onDragOver: (e) => { e.preventDefault(); if (id !== kpiDrag) setKpiDragOver(id); },
        onDrop: (e) => {
            e.preventDefault();
            if (!kpiDrag || kpiDrag === id) return;
            const cards = [...dashboardConfig.kpiCards];
            const fi = cards.findIndex(c => c.id === kpiDrag), ti = cards.findIndex(c => c.id === id);
            const [m] = cards.splice(fi, 1); cards.splice(ti, 0, m);
            onConfigChange({ ...dashboardConfig, kpiCards: cards });
            setKpiDrag(null); setKpiDragOver(null);
        },
        onDragEnd: () => { setKpiDrag(null); setKpiDragOver(null); }
    });

    const availableCatalog = Object.entries(KPI_CATALOG).filter(([k, v]) => !dashboardConfig.kpiCards.some(c => c.metricKey === k) && (!search || v.defaultTitle.toLowerCase().includes(search.toLowerCase())));

    return (
        <>
            <div className={`customize-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />
            <aside className={`customize-panel ${isOpen ? 'open' : ''}`}>
                <div className="customize-panel-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <FaScrewdriverWrench style={{ fontSize: '1.1rem', opacity: .85 }} />
                        <h2>Personalizar Dashboard</h2>
                    </div>
                    <button className="customize-close-btn" onClick={onClose}><FaXmark /></button>
                </div>

                <div className="customize-tabs">
                    {[{ k: 'layout', l: 'Layout', i: <FaTableCells /> }, { k: 'kpis', l: 'KPIs', i: <FaChartBar /> }, { k: 'notes', l: 'Notas', i: <FaCircleInfo /> }].map(t => (
                        <button key={t.k} className={`customize-tab-btn ${tab === t.k ? 'active' : ''}`} onClick={() => setTab(t.k)}>{t.i} {t.l}</button>
                    ))}
                </div>

                <div className="customize-panel-body">
                    {/* LAYOUT TAB */}
                    {tab === 'layout' && (
                        <div>
                            <p className="customize-hint">Arraste para reordenar · 👁 para mostrar/ocultar · edite o título na caixa de texto</p>
                            <ul className="section-toggle-list">
                                {sorted.map(key => {
                                    const meta = SECTION_META[key];
                                    const sec = dashboardConfig.sections[key];
                                    return (
                                        <li key={key} className={`section-toggle-item ${!sec.visible ? 'hidden' : ''} ${secDrag === key ? 'dragging' : ''} ${secDragOver === key ? 'drag-over' : ''}`} {...secDragHandlers(key)}>
                                            <FaGripVertical className="drag-handle" />
                                            <span className="section-item-icon">{meta.icon}</span>
                                            <input
                                                type="text"
                                                className="section-title-input"
                                                placeholder={meta.label}
                                                value={dashboardConfig.sectionTitles?.[key] || ''}
                                                onChange={e => onConfigChange({ ...dashboardConfig, sectionTitles: { ...dashboardConfig.sectionTitles, [key]: e.target.value } })}
                                            />
                                            <button className={`toggle-visibility-btn ${sec.visible ? 'visible' : 'hidden'}`} onClick={() => onConfigChange({ ...dashboardConfig, sections: { ...dashboardConfig.sections, [key]: { ...sec, visible: !sec.visible } } })}>
                                                {sec.visible ? <FaEye /> : <FaEyeSlash />}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    {/* KPIS TAB */}
                    {tab === 'kpis' && (
                        <div>
                            <h3 className="customize-section-title">KPIs Ativos</h3>
                            <p className="customize-hint">Arraste para reordenar</p>
                            {dashboardConfig.kpiCards.length === 0 && <p className="no-data-text" style={{ marginBottom: 16 }}>Nenhum KPI ativo. Adicione abaixo.</p>}
                            <ul className="kpi-builder-list">
                                {dashboardConfig.kpiCards.map(card => {
                                    const cat = KPI_CATALOG[card.metricKey];
                                    if (!cat) return null;
                                    return (
                                        <li key={card.id} className={`kpi-builder-item ${kpiDrag === card.id ? 'dragging' : ''} ${kpiDragOver === card.id ? 'drag-over' : ''}`} {...kpiDragHandlers(card.id)}>
                                            <FaGripVertical className="drag-handle" />
                                            <span className="kpi-builder-icon"><KpiIcon type={cat.icon} /></span>
                                            <span className="kpi-builder-label">{card.customTitle || cat.defaultTitle}</span>
                                            <button className="kpi-remove-btn" onClick={() => onConfigChange({ ...dashboardConfig, kpiCards: dashboardConfig.kpiCards.filter(c => c.id !== card.id) })}><FaTrash /></button>
                                        </li>
                                    );
                                })}
                            </ul>

                            <h3 className="customize-section-title" style={{ marginTop: 24 }}>Adicionar Métrica</h3>
                            <div className="kpi-search-wrapper">
                                <FaMagnifyingGlass className="kpi-search-icon" />
                                <input type="text" className="kpi-search-input" placeholder="Buscar métrica..." value={search} onChange={e => setSearch(e.target.value)} />
                            </div>
                            <div className="kpi-catalog-grid">
                                {availableCatalog.map(([key, cat]) => (
                                    <button key={key} className="kpi-catalog-item" onClick={() => onConfigChange({ ...dashboardConfig, kpiCards: [...dashboardConfig.kpiCards, { id: genId(), metricKey: key }] })}>
                                        <span className="kpi-catalog-icon"><KpiIcon type={cat.icon} /></span>
                                        <span className="kpi-catalog-label">{cat.defaultTitle}</span>
                                        <FaPlus className="kpi-catalog-plus" />
                                    </button>
                                ))}
                                {availableCatalog.length === 0 && <p className="no-data-text">Todos os KPIs disponíveis já foram adicionados.</p>}
                            </div>
                        </div>
                    )}

                    {/* NOTES TAB */}
                    {tab === 'notes' && (
                        <div>
                            <p className="customize-hint">Adicione notas de contexto visíveis no topo de cada seção do dashboard.</p>
                            {sorted.map(key => {
                                const meta = SECTION_META[key];
                                const note = dashboardConfig.annotations?.[key] || '';
                                return (
                                    <div key={key} className="note-editor-block">
                                        <label className="note-editor-label">{meta.icon} {dashboardConfig.sectionTitles?.[key] || meta.label}</label>
                                        <textarea className="note-textarea" placeholder={`Nota para "${meta.label}"...`} value={note} onChange={e => onConfigChange({ ...dashboardConfig, annotations: { ...dashboardConfig.annotations, [key]: e.target.value } })} rows={3} />
                                        {note && <button className="clear-note-btn" onClick={() => onConfigChange({ ...dashboardConfig, annotations: { ...dashboardConfig.annotations, [key]: '' } })}>× Limpar</button>}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="customize-panel-footer">
                    <button className="customize-reset-btn" onClick={onReset}><FaRotateLeft /> Redefinir</button>
                    <button className="customize-save-btn" onClick={onSave}><FaFloppyDisk /> Salvar</button>
                </div>
            </aside>
        </>
    );
};

// =============================================================================
// === MAIN ====================================================================
// =============================================================================

const PeopleAnalytics = ({ userData }) => {
    const [allUsers, setAllUsers] = useState([]);
    const [allFeedbacks, setAllFeedbacks] = useState([]);
    const [allAbsences, setAllAbsences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({ department: 'all', role: 'all', store: 'all' });
    const [period, setPeriod] = useState({ preset: '12m', customStart: '', customEnd: '' });
    const [aiPrompt, setAiPrompt] = useState('');
    const [isGenWidget, setIsGenWidget] = useState(false);
    const [customWidgets, setCustomWidgets] = useState([]);
    const [nineBoxCfg, setNineBoxCfg] = useState({ x: 'tempoCasaMeses', y: 'salario' });
    const [dashCfg, setDashCfg] = useState(loadCfg);
    const [isCustomizing, setIsCustomizing] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [savedOk, setSavedOk] = useState(false);
    const [editingKpi, setEditingKpi] = useState(null);
    const [kpiDrag, setKpiDrag] = useState(null);
    const [kpiDragOver, setKpiDragOver] = useState(null);

    const fetchData = useCallback(async () => {
        if (!userData || userData.departamento !== 'People') { setError('Acesso restrito ao departamento de Pessoas.'); setLoading(false); return; }
        setLoading(true);
        try {
            const snap = await getDocs(query(collection(db, 'users'), where('empresa_id', '==', userData.empresa_id)));
            let list = snap.docs.map(d => ({ ...d.data(), id: d.id }));
            list = await Promise.all(list.map(async u => { const p = await getDoc(doc(db, 'usersprivado', u.id)); return { ...u, ...(p.exists() ? p.data() : {}) }; }));
            const cs = await getDocs(query(collection(db, 'Empresas', userData.empresa_id, 'evaluationCycles'), orderBy('createdAt', 'desc'), limit(1)));
            if (!cs.empty) {
                const ps = await getDocs(collection(db, 'Empresas', userData.empresa_id, 'evaluationCycles', cs.docs[0].id, 'participants'));
                const sc = {}; ps.forEach(d => { const dt = d.data(); if (dt.finalScore != null) sc[dt.userId] = dt.finalScore; });
                list = list.map(u => ({ ...u, notaAvaliacao: sc[u.id] ?? null }));
            }
            setAllUsers(list);
            const fb = await getDocs(query(collection(db, 'Empresas', userData.empresa_id, 'feedbacks')));
            setAllFeedbacks(fb.docs.map(d => d.data()));
            const ab = await getDocs(query(collection(db, 'Empresas', userData.empresa_id, 'absenteismo')));
            setAllAbsences(ab.docs.map(d => ({ ...d.data(), id: d.id })));
        } catch (e) { setError('Erro ao carregar dados.'); console.error(e); }
        finally { setLoading(false); }
    }, [userData]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleGenWidget = async () => {
        if (!aiPrompt.trim()) return;
        setIsGenWidget(true);
        try {
            const fn = httpsCallable(getFunctions(getApp(), 'southamerica-east1'), 'generateAnalyticsWidget');
            const { start, end, label } = getPeriod(period);
            const enriched = allUsers.map(u => { const e = getDateObject(u.dataEntrada), b = getDateObject(u.dataNascimento); return { ...u, tempoCasaMeses: e ? Math.floor((new Date() - e) / (1000 * 60 * 60 * 24 * 30)) : 0, dataNascimento: b?.toISOString() ?? null, dataEntrada: e?.toISOString() ?? null }; });
            const r = await fn({ prompt: aiPrompt, dataset: enriched, period: { start: start.toISOString(), end: end.toISOString(), label } });
            if (r.data.success) { setCustomWidgets(prev => [r.data.widget, ...prev]); setAiPrompt(''); }
        } catch (e) { alert('Erro ao gerar: ' + e.message); }
        finally { setIsGenWidget(false); }
    };

    const saveCfg = () => {
        try { localStorage.setItem('pa_cfg_v3', JSON.stringify(dashCfg)); setSavedOk(true); setTimeout(() => setSavedOk(false), 2800); }
        catch (e) { console.error(e); }
    };

    const data = useMemo(() => {
        if (!allUsers.length) return null;
        const { start: pS, end: pE, label: pL } = getPeriod(period);
        const fil = allUsers.filter(u => (filters.department === 'all' || u.departamento === filters.department) && (filters.role === 'all' || u.funcao === filters.role) && (filters.store === 'all' || u.loja === filters.store));
        const active = fil.filter(u => u.status === 'active');
        const now = new Date();

        const ae = active.map(u => {
            const e = getDateObject(u.dataEntrada), b = getDateObject(u.dataNascimento);
            const tempoCasaMeses = e ? Math.floor((now - e) / (1000 * 60 * 60 * 24 * 30)) : 0;
            const idade = b ? Math.floor((now - b) / (1000 * 60 * 60 * 24 * 365.25)) : 0;
            const salario = Number(u.salario) || 0;
            const feedbacksRecebidos = allFeedbacks.filter(fb => fb.destinatarioId === u.id).length;
            const horasPerdidas = allAbsences.filter(a => a.userId === u.id).reduce((s, a) => s + (parseFloat(a.hours) || 0), 0);
            const hs = (u.termometroHumor || []).map(h => ({ 'muito-feliz': 5, feliz: 4, neutro: 3, triste: 2, 'muito-triste': 1 }[h.humor] || 0)).filter(s => s > 0);
            const humorMedio = hs.length ? parseFloat((hs.reduce((a, b) => a + b, 0) / hs.length).toFixed(1)) : null;
            return { ...u, tempoCasaMeses, idade, salario, feedbacksRecebidos, horasPerdidas, humorMedio, notaAvaliacao: u.notaAvaliacao != null ? Number(u.notaAvaliacao) : null };
        });

        const inactive = fil.filter(u => { if (u.status !== 'inactive') return false; const d = getDateObject(u.dataInativacao); return d && d >= pS && d <= pE; });
        const hc = active.length;
        const avgSalary = hc ? active.reduce((s, u) => s + (u.salario || 0), 0) / hc : 0;
        const totalSalaryMass = active.reduce((s, u) => s + (u.salario || 0), 0);
        const ms = active.flatMap(u => (u.termometroHumor || []).map(m => ({ 'muito-feliz': 5, feliz: 4, neutro: 3, triste: 2, 'muito-triste': 1 }[m.humor] || 0)).filter(s => s > 0));
        const avgMood = ms.length ? (ms.reduce((a, b) => a + b, 0) / ms.length).toFixed(1) : 'N/A';
        const feedbacksTotal = allFeedbacks.filter(fb => active.some(u => u.id === fb.destinatarioId)).length;
        const avgTenureMonths = hc ? Math.round(ae.reduce((s, u) => s + u.tempoCasaMeses, 0) / hc) : 0;
        const evalSc = ae.filter(u => u.notaAvaliacao != null).map(u => u.notaAvaliacao);
        const avgEvalScore = evalSc.length ? parseFloat((evalSc.reduce((a, b) => a + b, 0) / evalSc.length).toFixed(2)) : null;
        const cm = now.getUTCMonth();
        const rampUpList = active.filter(u => { const e = getDateObject(u.dataEntrada); return e && e >= pS && e <= pE; }).map(u => ({ name: u.name, photoURL: u.photoURL, detail: `Entrou: ${formatDateVisual(u.dataEntrada)}` }));
        const monthlyBirthdays = active.filter(u => { const b = getDateObject(u.dataNascimento); return b && b.getUTCMonth() === cm; }).map(u => ({ name: u.name, photoURL: u.photoURL, detail: formatDateVisual(u.dataNascimento, { day: '2-digit', month: 'long', timeZone: 'UTC' }) }));
        const workAnniversaries = active.filter(u => { const e = getDateObject(u.dataEntrada); return e && e.getUTCMonth() === cm && e.getUTCFullYear() < now.getUTCFullYear(); }).map(u => { const e = getDateObject(u.dataEntrada); const y = now.getUTCFullYear() - e.getUTCFullYear(); return { name: u.name, photoURL: u.photoURL, detail: `${y} ano${y > 1 ? 's' : ''} (${formatDateVisual(e)})` }; });
        const topGivers = Object.entries(allFeedbacks.reduce((acc, fb) => { if (fb.autorNome) acc[fb.autorNome] = (acc[fb.autorNome] || 0) + 1; return acc; }, {})).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name, count]) => ({ name, detail: `${count} feedbacks` }));
        const absStats = calcAbsent(allAbsences.filter(a => active.some(u => u.id === a.userId)), hc, pS, pE);
        const absTypesData = Object.entries(absStats.data.reduce((acc, a) => { const l = { falta_injustificada: 'Falta Injustificada', atestado: 'Atestado', atraso: 'Atraso', saida_antecipada: 'Saída Antecipada', licenca: 'Licença', suspensao: 'Suspensão' }[a.type] || a.type; acc[l] = (acc[l] || 0) + 1; return acc; }, {})).map(([name, value]) => ({ name, value }));
        const absByDeptData = Object.entries(absStats.data.reduce((acc, a) => { const u = allUsers.find(u => u.id === a.userId); acc[u?.departamento || 'N/A'] = (acc[u?.departamento || 'N/A'] || 0) + parseFloat(a.hours); return acc; }, {})).map(([name, value]) => ({ name, value }));
        const exitReasonsData = Object.entries(inactive.reduce((acc, u) => { if (u.exitData?.reason) { const l = { salary: 'Proposta Salarial', career: 'Falta de Crescimento', leadership: 'Liderança', culture: 'Cultura', performance: 'Baixo Desempenho', restructuring: 'Reestruturação', personal: 'Pessoal', other: 'Outros' }[u.exitData.reason] || 'Outros'; acc[l] = (acc[l] || 0) + 1; } return acc; }, {})).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
        const exitTypesData = Object.entries(inactive.reduce((acc, u) => { if (u.exitData?.type) { const l = { voluntary: 'Voluntário', involuntary: 'Involuntário', contract_end: 'Fim de Contrato' }[u.exitData.type] || 'Outros'; acc[l] = (acc[l] || 0) + 1; } return acc; }, {})).map(([name, value]) => ({ name, value }));
        const exitRecommendationData = Object.entries(inactive.reduce((acc, u) => { if (u.exitData?.wouldRecommend) { const l = { yes: 'Sim', no: 'Não', maybe: 'Talvez' }[u.exitData.wouldRecommend] || 'N/A'; acc[l] = (acc[l] || 0) + 1; } return acc; }, {})).map(([name, value]) => ({ name, value }));
        const exitRolesData = Object.entries(inactive.reduce((acc, u) => { acc[u.funcao || 'N/I'] = (acc[u.funcao || 'N/I'] || 0) + 1; return acc; }, {})).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
        const vt = inactive.filter(u => u.exitData?.type === 'voluntary').length;
        const voluntaryTurnover = inactive.length ? ((vt / inactive.length) * 100).toFixed(0) + '%' : '0%';
        const avgTenureLeavers = inactive.length ? (inactive.reduce((s, u) => { const e = getDateObject(u.dataEntrada), x = getDateObject(u.dataInativacao); return s + (e && x ? x.getMonth() - e.getMonth() + 12 * (x.getFullYear() - e.getFullYear()) : 0); }, 0) / inactive.length).toFixed(1) + ' meses' : '-';
        const lostSalaries = inactive.reduce((s, u) => s + (u.salario || 0), 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

        return {
            periodLabel: pL,
            kpis: { headcount: hc, turnoverRate: calcTurnover(fil, pS, pE), avgSalary, totalSalaryMass, avgMood, feedbacksTotal, avgTenureMonths, avgEvalScore, absenteeismRate: absStats.rate, lostHours: absStats.lostHours, absencesCount: absStats.count, rampUpCount: rampUpList.length, rampUpList, monthlyBirthdays, workAnniversaries, topGivers, exitCount: inactive.length },
            charts: {
                headcountByDeptData: Object.entries(active.reduce((a, u) => { const k = u.departamento || 'N/A'; a[k] = (a[k] || 0) + 1; return a; }, {})).map(([name, value]) => ({ name, value })),
                headcountOverTimeData: getHCOverTime(fil, pS, pE),
                roleDistributionData: Object.entries(active.reduce((a, u) => { const k = u.funcao || 'N/A'; a[k] = (a[k] || 0) + 1; return a; }, {})).map(([name, value]) => ({ name, value })),
                exitReasonsData, exitTypesData, exitRecommendationData, exitRolesData, absTypesData, absByDeptData
            },
            insights: { topExitReason: exitReasonsData[0]?.name || 'N/A', voluntaryTurnover, avgTenureLeavers, lostSalaries },
            activeUsersEnriched: ae,
        };
    }, [allUsers, allFeedbacks, allAbsences, filters, period]);

    if (loading) return <div className="loading-container"><div className="spinner" /><p>Carregando dados...</p></div>;
    if (error) return <div className="error-container">{error}</div>;

    const uniqDepts = [...new Set(allUsers.map(u => u.departamento).filter(Boolean))];
    const uniqRoles = [...new Set(allUsers.map(u => u.funcao).filter(Boolean))];
    const uniqStores = [...new Set(allUsers.map(u => u.loja).filter(Boolean))];
    const sortedSecs = Object.entries(dashCfg.sections).sort((a, b) => a[1].order - b[1].order).map(([k]) => k);
    const pDates = getPeriod(period);
    const pLabel = data?.periodLabel || '';

    const getSecTitle = (k) => dashCfg.sectionTitles?.[k] || SECTION_META[k].label;
    const getNote = (k) => dashCfg.annotations?.[k];

    // In-dashboard KPI drag
    const kpiDragHandlers = (id) => ({
        draggable: isEditMode,
        onDragStart: isEditMode ? e => { setKpiDrag(id); e.dataTransfer.effectAllowed = 'move'; } : undefined,
        onDragOver: isEditMode ? e => { e.preventDefault(); if (id !== kpiDrag) setKpiDragOver(id); } : undefined,
        onDrop: isEditMode ? e => {
            e.preventDefault();
            if (!kpiDrag || kpiDrag === id) return;
            const cards = [...dashCfg.kpiCards];
            const fi = cards.findIndex(c => c.id === kpiDrag), ti = cards.findIndex(c => c.id === id);
            const [m] = cards.splice(fi, 1); cards.splice(ti, 0, m);
            setDashCfg(prev => ({ ...prev, kpiCards: cards }));
            setKpiDrag(null); setKpiDragOver(null);
        } : undefined,
        onDragEnd: isEditMode ? () => { setKpiDrag(null); setKpiDragOver(null); } : undefined,
    });

    const SectionHeader = ({ sectionKey, accentColor }) => {
        const note = getNote(sectionKey);
        return (
            <div>
                <h3 className="section-title-small" style={accentColor ? { borderColor: accentColor } : {}}>
                    {getSecTitle(sectionKey)}
                    {pLabel && <span className="period-badge">{pLabel}</span>}
                    {isEditMode && <span className="em-indicator">✏️ edição</span>}
                </h3>
                {note && <AnnotationBlock text={note} isEditMode={isEditMode} onEdit={() => setIsCustomizing(true)} />}
            </div>
        );
    };

    const renderSection = (key) => {
        if (!dashCfg.sections[key]?.visible) return null;
        switch (key) {
            case 'kpiNumbers': return (
                <div key={key} className="pa-section">
                    {getNote(key) && <AnnotationBlock text={getNote(key)} isEditMode={isEditMode} onEdit={() => setIsCustomizing(true)} />}
                    <div className="kpi-grid-numbers">
                        {dashCfg.kpiCards.map(card => {
                            const cat = KPI_CATALOG[card.metricKey];
                            if (!cat) return null;
                            return (
                                <div key={card.id} className={`kpi-drag-wrapper ${isEditMode && kpiDrag === card.id ? 'kpi-dragging' : ''} ${isEditMode && kpiDragOver === card.id ? 'kpi-dragover' : ''}`} {...kpiDragHandlers(card.id)}>
                                    <KpiCard
                                        title={card.customTitle || cat.defaultTitle}
                                        value={cat.getValue(data)}
                                        icon={<KpiIcon type={cat.icon} />}
                                        status={cat.getStatus(data)}
                                        isEditMode={isEditMode}
                                        onDelete={() => setDashCfg(prev => ({ ...prev, kpiCards: prev.kpiCards.filter(c => c.id !== card.id) }))}
                                        onEdit={() => setEditingKpi(card)}
                                    />
                                </div>
                            );
                        })}
                        {isEditMode && (
                            <button className="add-kpi-btn" onClick={() => setIsCustomizing(true)}>
                                <FaPlus /> Adicionar KPI
                            </button>
                        )}
                    </div>
                </div>
            );

            case 'highlights': return (
                <div key={key} className="pa-section">
                    <SectionHeader sectionKey={key} />
                    <div className="kpi-grid-lists">
                        <DetailedListCard title={`Novas Entradas (${data?.kpis.rampUpCount})`} icon={<FaRocket />} items={data?.kpis.rampUpList || []} detailKey="detail" />
                        <DetailedListCard title="Aniversariantes (Mês)" icon={<FaCakeCandles />} items={data?.kpis.monthlyBirthdays || []} />
                        <DetailedListCard title="Tempo de Casa (Mês)" icon={<FaBuilding />} items={data?.kpis.workAnniversaries || []} />
                        <DetailedListCard title="Top Feedbacks" icon={<FaThumbsUp />} items={data?.kpis.topGivers || []} detailKey="detail" />
                    </div>
                </div>
            );

            case 'nineBox': return (
                <div key={key} className="pa-section">
                    <SectionHeader sectionKey={key} accentColor="#8e44ad" />
                    <div className="chart-card" style={{ marginBottom: '2rem' }}>
                        <h3 className="chart-title" style={{ margin: '0 0 6px' }}>Cruzamento Dinâmico</h3>
                        <p style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 1.5rem' }}>Selecione dois eixos para distribuir a equipe. Os quadrantes são calculados automaticamente com base nos extremos dos dados reais.</p>
                        <DynamicNineBox users={data?.activeUsersEnriched || []} config={nineBoxCfg} setConfig={setNineBoxCfg} />
                    </div>
                </div>
            );

            case 'standardCharts': return (
                <div key={key} className="pa-section">
                    <SectionHeader sectionKey={key} />
                    <div className="charts-grid-full">
                        <ChartCard title={`Evolução do Headcount (${pLabel})`}>
                            <ResponsiveContainer width="100%" height={350}>
                                <LineChart data={data?.charts.headcountOverTimeData} margin={{ top: 5, right: 30, left: 20, bottom: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} interval={0} />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Legend verticalAlign="top" />
                                    <Line type="monotone" dataKey="headcount" name="Colaboradores" stroke="var(--primary-color, #002454)" strokeWidth={2} activeDot={{ r: 8 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </ChartCard>
                        <div className="charts-column">
                            <ChartCard title="Colaboradores por Depto.">
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart data={data?.charts.headcountByDeptData} layout="vertical" margin={{ top: 5, right: 40, left: 40, bottom: 5 }}>
                                        <XAxis type="number" hide />
                                        <YAxis type="category" dataKey="name" width={120} tickLine={false} axisLine={false} />
                                        <Tooltip cursor={{ fill: '#f4f4f4' }} />
                                        <Bar dataKey="value" name="Colaboradores" fill="var(--secondary-color, #8dbefd)" barSize={20} radius={[0, 10, 10, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartCard>
                            <ChartCard title="Distribuição de Cargos">
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <ResponsiveContainer width="60%" height={250}>
                                        <PieChart>
                                            <Pie data={data?.charts.roleDistributionData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} dataKey="value" paddingAngle={5}>
                                                {data?.charts.roleDistributionData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div style={{ width: '40%', maxHeight: 250, overflowY: 'auto', fontSize: '0.8rem' }}>
                                        {data?.charts.roleDistributionData.map((e, i) => (
                                            <div key={i} style={{ marginBottom: 5, display: 'flex', alignItems: 'center' }}>
                                                <span style={{ width: 10, height: 10, borderRadius: '50%', background: PIE_COLORS[i % PIE_COLORS.length], marginRight: 5, flexShrink: 0, display: 'inline-block' }} />
                                                {e.name}: <strong style={{ marginLeft: 4 }}>{e.value}</strong>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </ChartCard>
                        </div>
                    </div>
                </div>
            );

            case 'absenteeism': return (
                <div key={key} className="pa-section">
                    <SectionHeader sectionKey={key} accentColor="#e67e22" />
                    <div className="kpi-grid-numbers">
                        <KpiCard title="Taxa de Absenteísmo" value={`${data?.kpis.absenteeismRate || 0}%`} icon={<FaCalendarMinus />} status={parseFloat(data?.kpis.absenteeismRate) > 5 ? 'alert' : parseFloat(data?.kpis.absenteeismRate) > 2 ? 'warning' : 'good'} />
                        <KpiCard title="Horas Perdidas" value={`${data?.kpis.lostHours || 0}h`} icon={<IoTimerOutline />} />
                        <KpiCard title="Ocorrências" value={data?.kpis.absencesCount || 0} icon={<FaFileExcel />} />
                    </div>
                    <div className="charts-grid-full">
                        <ChartCard title="Motivos de Ausência">
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie data={data?.charts.absTypesData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label>
                                        {data?.charts.absTypesData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip /><Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </ChartCard>
                        <ChartCard title="Horas Perdidas por Depto.">
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={data?.charts.absByDeptData} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" /><YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                                    <Tooltip cursor={{ fill: '#f4f4f4' }} />
                                    <Bar dataKey="value" name="Horas" fill="#e67e22" radius={[0, 4, 4, 0]} barSize={20} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>
                </div>
            );

            case 'exits': return (
                <div key={key} className="pa-section">
                    <SectionHeader sectionKey={key} />
                    <div className="kpi-grid-numbers" style={{ marginBottom: '1.5rem' }}>
                        {[['Principal Motivo de Saída', data?.insights.topExitReason, '#e74c3c'], ['Turnover Voluntário', data?.insights.voluntaryTurnover, '#f39c12'], ['Impacto Financeiro', data?.insights.lostSalaries, null], ['Tempo Médio (Desligados)', data?.insights.avgTenureLeavers, null]].map(([t, v, c]) => (
                            <div key={t} className="kpi-card">
                                <div className="kpi-content">
                                    <span className="kpi-title">{t}</span>
                                    <div className="kpi-value" style={{ fontSize: '1.1rem', ...(c ? { color: c } : {}) }}>{v}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="charts-grid-full">
                        <div className="charts-column" style={{ flex: 2 }}>
                            <ChartCard title="Motivos de Saída" icon={<FaDoorOpen />}>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={data?.charts.exitReasonsData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                        <XAxis type="number" allowDecimals={false} />
                                        <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} />
                                        <Tooltip cursor={{ fill: '#f4f4f4' }} />
                                        <Bar dataKey="value" name="Ex-Colaboradores" fill="#e74c3c" radius={[0, 4, 4, 0]} barSize={20} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartCard>
                            <ChartCard title="Desligamentos por Cargo" icon={<FaBriefcase />}>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={data?.charts.exitRolesData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                        <XAxis type="number" allowDecimals={false} />
                                        <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} />
                                        <Tooltip cursor={{ fill: '#f4f4f4' }} />
                                        <Bar dataKey="value" name="Ex-Colaboradores" fill="#e67e22" radius={[0, 4, 4, 0]} barSize={20} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </div>
                        <div className="charts-column" style={{ flex: 1 }}>
                            <ChartCard title="Tipo de Desligamento">
                                <ResponsiveContainer width="100%" height={250}>
                                    <PieChart>
                                        <Pie data={data?.charts.exitTypesData} cx="50%" cy="50%" innerRadius={50} outerRadius={70} dataKey="value" paddingAngle={5} label>
                                            {data?.charts.exitTypesData.map((_, i) => <Cell key={i} fill={['#e74c3c', '#f39c12', '#95a5a6'][i % 3]} />)}
                                        </Pie>
                                        <Tooltip /><Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </ChartCard>
                            <ChartCard title="Recomendaria a Empresa?">
                                <ResponsiveContainer width="100%" height={250}>
                                    <PieChart>
                                        <Pie data={data?.charts.exitRecommendationData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label>
                                            {data?.charts.exitRecommendationData.map((e, i) => <Cell key={i} fill={e.name === 'Sim' ? '#2ecc71' : e.name === 'Não' ? '#e74c3c' : '#f1c40f'} />)}
                                        </Pie>
                                        <Tooltip /><Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </div>
                    </div>
                </div>
            );
            default: return null;
        }
    };

    return (
        <div className="analytics-dashboard">
            {/* HEADER */}
            <header className="analytics-header">
                <div>
                    <h1>People Analytics</h1>
                    <p>Insights estratégicos sobre o capital humano da empresa.</p>
                </div>
                <div className="header-actions">
                    <button className={`edit-mode-btn ${isEditMode ? 'active' : ''}`} onClick={() => setIsEditMode(v => !v)}>
                        <FaPencil /> {isEditMode ? 'Sair da Edição' : 'Editar'}
                    </button>
                    <button className="customize-trigger-btn" onClick={() => setIsCustomizing(true)}>
                        <FaGear /> Personalizar
                    </button>
                </div>
            </header>

            {isEditMode && (
                <div className="edit-mode-banner">
                    <FaPencil /> Modo de edição ativo — arraste KPIs para reorganizar, use ✏️ para editar título/métrica e 🗑️ para remover.
                </div>
            )}

            {savedOk && <div className="save-notification"><FaCheck /> Layout salvo com sucesso!</div>}

            {/* AI */}
            <div className="ai-widget-generator">
                <div className="ai-input-wrapper">
                    <FaWandMagicSparkles className="ai-icon" />
                    <input type="text" placeholder="Ex: Relação entre salário e tempo de casa, Quem são os top performers?" value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleGenWidget()} />
                    <button onClick={handleGenWidget} disabled={isGenWidget || !aiPrompt.trim()}>{isGenWidget ? 'Gerando...' : 'Gerar Insight'}</button>
                </div>
            </div>

            {customWidgets.length > 0 && (
                <div className="custom-widgets-section">
                    <h3 className="section-title-small">Meus Insights (IA)</h3>
                    <div className="charts-grid-full">
                        {customWidgets.map((w, i) => (
                            <ChartCard key={i} title={w.title} description={w.description} icon={<FaWandMagicSparkles style={{ color: '#8b5cf6' }} />} onDelete={() => setCustomWidgets(p => p.filter((_, j) => j !== i))}>
                                <DynamicWidget config={w} />
                            </ChartCard>
                        ))}
                    </div>
                </div>
            )}

            {/* PERIOD FILTER */}
            <div className="period-filter-bar">
                <div className="period-filter-left">
                    <FaCalendarDays className="period-icon" />
                    <span className="period-label-text">Período:</span>
                    <div className="period-presets">
                        {PERIOD_PRESETS.map(p => <button key={p.key} className={`period-preset-btn ${period.preset === p.key ? 'active' : ''}`} onClick={() => setPeriod(prev => ({ ...prev, preset: p.key }))}>{p.label}</button>)}
                    </div>
                </div>
                {period.preset === 'custom' ? (
                    <div className="custom-date-range">
                        <label>De:</label><input type="date" value={period.customStart} onChange={e => setPeriod(p => ({ ...p, customStart: e.target.value }))} className="date-input" />
                        <label>Até:</label><input type="date" value={period.customEnd} onChange={e => setPeriod(p => ({ ...p, customEnd: e.target.value }))} className="date-input" max={new Date().toISOString().split('T')[0]} />
                    </div>
                ) : <span className="period-range-text">{pDates.start.toLocaleDateString('pt-BR')} – {pDates.end.toLocaleDateString('pt-BR')}</span>}
            </div>

            {/* FILTERS */}
            <div className="filters-bar">
                <FaFilter className="filter-icon" />
                {[{ l: 'LOJA', k: 'store', opts: uniqStores }, { l: 'DEPARTAMENTO', k: 'department', opts: uniqDepts }, { l: 'CARGO', k: 'role', opts: uniqRoles }].map(({ l, k, opts }) => (
                    <div key={k} className="filter-item">
                        <label>{l}</label>
                        <select value={filters[k]} onChange={e => setFilters(prev => ({ ...prev, [k]: e.target.value }))}>
                            <option value="all">Todos</option>
                            {opts.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                ))}
            </div>

            {/* SECTIONS */}
            {sortedSecs.map(renderSection)}

            {/* KPI EDIT MODAL */}
            {editingKpi && (
                <KpiEditModal
                    card={editingKpi}
                    onClose={() => setEditingKpi(null)}
                    onSave={updated => { setDashCfg(prev => ({ ...prev, kpiCards: prev.kpiCards.map(c => c.id === updated.id ? updated : c) })); setEditingKpi(null); }}
                />
            )}

            {/* CUSTOMIZE PANEL */}
            <CustomizePanel
                isOpen={isCustomizing}
                onClose={() => setIsCustomizing(false)}
                dashboardConfig={dashCfg}
                onConfigChange={setDashCfg}
                onSave={() => { saveCfg(); setIsCustomizing(false); }}
                onReset={() => setDashCfg(DEFAULT_DASHBOARD_CONFIG)}
            />
        </div>
    );
};

export default PeopleAnalytics;